import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { apiFetch } from './client'
import { ApiError } from './client'
import {
  AlertsListResponse,
  AnalysisAreaResponse,
  AnalysisDetail,
  AnalysisHours,
  BriefEvidence,
  BriefFeedback,
  CameraActivityResponse,
  CameraAnalysesResponse,
  CameraBaseline,
  CameraDetailResponse,
  CameraEventsResponse,
  ChatConversationsResponse,
  ChatHistoryResponse,
  type DisplayCropContext,
  DisplayCropResponse,
  EdgeDevice,
  EventDetailResponse,
  LlmStats,
  MaxSuggestionsResponse,
  PhaseSeenResponse,
  SiteWatchResponse,
  SitePlanResponse,
  LiaMapResponse,
  ScheduleVersionsResponse,
  SiteAnalysesResponse,
  SiteCapturesResponse,
  SiteEventsResponse,
  SitePayload,
  SitesListResponse,
  StashedViewPayload,
} from './schemas'

const POLL_INTERVAL_MS = 30_000

export const queryKeys = {
  sites: () => ['sites'] as const,
  /** Prefix for every site-scoped cache entry. `siteQuery` appends `{date}`
   *  to this prefix so that optimistic updates and invalidations written
   *  against `queryKeys.site(siteId)` fan out to every date view. */
  site: (siteId: string) => ['sites', siteId] as const,
  siteDate: (siteId: string, date: string | null) => ['sites', siteId, { date }] as const,
  siteAlerts: (siteId: string) => ['sites', siteId, 'alerts'] as const,
  maxSuggestions: (siteId: string) => ['sites', siteId, 'max-suggestions'] as const,
  phaseSeen: (siteId: string, phaseId: number) => ['sites', siteId, 'phase-seen', phaseId] as const,
  siteWatch: (siteId: string) => ['sites', siteId, 'site-watch'] as const,
  sitePlan: (siteId: string) => ['sites', siteId, 'plan'] as const,
  lia: (siteId: string, range: 'today' | 'week') => ['sites', siteId, 'lia', range] as const,
  siteAnalyses: (siteId: string) => ['sites', siteId, 'analyses'] as const,
  siteAnalysesDate: (siteId: string, date: string | null) =>
    ['sites', siteId, 'analyses', { date }] as const,
  siteEvents: (siteId: string, limit: number, offset: number) =>
    ['sites', siteId, 'events', { limit, offset }] as const,
  event: (siteId: string, eventId: number, cam: string | null) =>
    ['sites', siteId, 'events', eventId, { cam }] as const,
  analysis: (siteId: string, type: string, id: number) =>
    ['sites', siteId, 'analyses', type, id] as const,
  siteEdges: (siteId: string) => ['sites', siteId, 'edges'] as const,
  camera: (siteId: string, cameraId: string) => ['sites', siteId, 'cameras', cameraId] as const,
  cameraEvents: (siteId: string, cameraId: string) =>
    ['sites', siteId, 'cameras', cameraId, 'events'] as const,
  cameraBaseline: (siteId: string, cameraId: string) =>
    ['sites', siteId, 'cameras', cameraId, 'baseline'] as const,
  cameraAnalyses: (siteId: string, cameraId: string) =>
    ['sites', siteId, 'cameras', cameraId, 'analyses'] as const,
  cameraAnalysisArea: (siteId: string, cameraId: string) =>
    ['sites', siteId, 'cameras', cameraId, 'analysis-area'] as const,
  cameraDisplayCrop: (siteId: string, cameraId: string, context: DisplayCropContext) =>
    ['sites', siteId, 'cameras', cameraId, 'display-crop', context] as const,
  cameraAnalysisHours: (siteId: string, cameraId: string) =>
    ['sites', siteId, 'cameras', cameraId, 'analysis-hours'] as const,
  cameraActivity: (siteId: string, cameraId: string, date: string | null) =>
    ['sites', siteId, 'cameras', cameraId, 'activity', { date }] as const,
  scheduleVersions: (siteId: string) => ['sites', siteId, 'schedule-versions'] as const,
  siteCaptures: (
    siteId: string,
    date: string | null,
    cameraId: string | null,
    eventIds: readonly number[] | null,
  ) => ['sites', siteId, 'captures', { date, cameraId, eventIds }] as const,
  llm: (from: string | null, to: string | null) => ['llm', { from, to }] as const,
  chatHistory: (conversationId: string) => ['chat', conversationId] as const,
  chatConversations: (siteId: string | null) => ['chat-conversations', siteId] as const,
  stashedView: (id: string) => ['chat', 'views', id] as const,
  dailyBriefEvidence: (siteId: string, date: string | null) =>
    ['sites', siteId, 'daily-brief', 'evidence', { date }] as const,
  weeklySummaryEvidence: (siteId: string, date: string | null) =>
    ['sites', siteId, 'weekly-summary', 'evidence', { date }] as const,
  briefFeedback: (siteId: string, briefType: string, periodStart: string, periodEnd: string) =>
    ['sites', siteId, 'brief-feedback', briefType, periodStart, periodEnd] as const,
}

// ── Portfolio ───────────────────────────────────────────────────

export const sitesQuery = queryOptions({
  queryKey: queryKeys.sites(),
  queryFn: () => apiFetch('/api/sites', SitesListResponse),
  refetchInterval: POLL_INTERVAL_MS,
})

// ── Site dashboard ──────────────────────────────────────────────

/** WEB-021: optional `date` (YYYY-MM-DD in site tz) reroutes the per-day
 *  fields (events_today, cameras[].events_today) to that day. Past windows
 *  are immutable, so we skip background polling when `date` is set. */
export const siteQuery = (siteId: string, date: string | null = null) =>
  queryOptions({
    queryKey: queryKeys.siteDate(siteId, date),
    queryFn: () => {
      const url = date
        ? `/api/sites/${encodeURIComponent(siteId)}?date=${encodeURIComponent(date)}`
        : `/api/sites/${encodeURIComponent(siteId)}`
      return apiFetch(url, SitePayload)
    },
    refetchInterval: date ? false : POLL_INTERVAL_MS,
  })

const EdgesResponse = z.object({ edges: z.array(EdgeDevice) })

export const siteEdgesQuery = (siteId: string) =>
  queryOptions({
    queryKey: queryKeys.siteEdges(siteId),
    queryFn: () => apiFetch(`/api/sites/${encodeURIComponent(siteId)}/edges`, EdgesResponse),
    refetchInterval: POLL_INTERVAL_MS,
  })

export const siteAlertsQuery = (siteId: string) =>
  queryOptions({
    queryKey: queryKeys.siteAlerts(siteId),
    queryFn: () => apiFetch(`/api/sites/${encodeURIComponent(siteId)}/alerts`, AlertsListResponse),
    refetchInterval: POLL_INTERVAL_MS,
  })

export const siteAnalysesQuery = (siteId: string, date: string | null = null) =>
  queryOptions({
    queryKey: queryKeys.siteAnalysesDate(siteId, date),
    queryFn: () => {
      const url = date
        ? `/api/sites/${encodeURIComponent(siteId)}/analyses?date=${encodeURIComponent(date)}`
        : `/api/sites/${encodeURIComponent(siteId)}/analyses`
      return apiFetch(url, SiteAnalysesResponse)
    },
    refetchInterval: date ? false : POLL_INTERVAL_MS,
  })

export const siteEventsQuery = (siteId: string, limit = 50, offset = 0) =>
  queryOptions({
    queryKey: queryKeys.siteEvents(siteId, limit, offset),
    queryFn: () =>
      apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/events?limit=${limit}&offset=${offset}`,
        SiteEventsResponse,
      ),
    refetchInterval: POLL_INTERVAL_MS,
  })

// ── Event / analysis details (drawers) ──────────────────────────

export const eventDetailQuery = (siteId: string, eventId: number, cam: string | null = null) =>
  queryOptions({
    queryKey: queryKeys.event(siteId, eventId, cam),
    queryFn: () => {
      const url = cam
        ? `/api/sites/${encodeURIComponent(siteId)}/events/${eventId}?cam=${encodeURIComponent(cam)}`
        : `/api/sites/${encodeURIComponent(siteId)}/events/${eventId}`
      return apiFetch(url, EventDetailResponse)
    },
  })

export const analysisDetailQuery = (siteId: string, type: string, id: number) =>
  queryOptions({
    queryKey: queryKeys.analysis(siteId, type, id),
    queryFn: () =>
      apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/analyses/${encodeURIComponent(type)}/${id}`,
        AnalysisDetail,
      ),
  })

// ── Camera ──────────────────────────────────────────────────────

/** WEB-021: optional `date` flips this to "that day's data" — closest-to-noon
 *  snapshot, that day's events, that day's batches. Past windows are immutable
 *  so we skip background polling when `date` is set. */
export const cameraQuery = (siteId: string, cameraId: string, date: string | null = null) =>
  queryOptions({
    queryKey: [...queryKeys.camera(siteId, cameraId), { date }] as const,
    queryFn: () => {
      const base = `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}`
      const url = date ? `${base}?date=${encodeURIComponent(date)}` : base
      return apiFetch(url, CameraDetailResponse)
    },
    refetchInterval: date ? false : POLL_INTERVAL_MS,
  })

export const cameraEventsQuery = (siteId: string, cameraId: string, date: string | null = null) =>
  queryOptions({
    queryKey: [...queryKeys.cameraEvents(siteId, cameraId), { date }] as const,
    queryFn: () => {
      const base = `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/events`
      const url = date ? `${base}?date=${encodeURIComponent(date)}` : base
      return apiFetch(url, CameraEventsResponse)
    },
    refetchInterval: date ? false : POLL_INTERVAL_MS,
  })

/** CDV-005: captures for the chat-driven "photos" sala body. Two modes:
 *   - curated: `eventIds` picks the exact set (date/cameraId ignored)
 *   - day-scoped: by `date` + optional `cameraId`. */
export const siteCapturesQuery = (
  siteId: string,
  date: string | null,
  cameraId: string | null = null,
  eventIds: readonly number[] | null = null,
) =>
  queryOptions({
    queryKey: queryKeys.siteCaptures(siteId, date, cameraId, eventIds),
    queryFn: () => {
      const qs = new URLSearchParams()
      if (eventIds && eventIds.length > 0) {
        qs.set('event_ids', eventIds.join(','))
      } else {
        if (date) qs.set('date', date)
        if (cameraId) qs.set('camera_id', cameraId)
      }
      const tail = qs.toString() ? `?${qs.toString()}` : ''
      return apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/captures${tail}`,
        SiteCapturesResponse,
      )
    },
    // Curated or past-day queries are immutable; only today polls.
    refetchInterval: (eventIds && eventIds.length > 0) || date ? false : POLL_INTERVAL_MS,
  })

/** Per-camera batches for one local day (today by default). Lives on the
 *  camera page — the site-level analyses list only surfaces cross-camera
 *  period summaries. */
export const cameraAnalysesQuery = (siteId: string, cameraId: string, date: string | null = null) =>
  queryOptions({
    queryKey: [...queryKeys.cameraAnalyses(siteId, cameraId), { date }] as const,
    queryFn: () => {
      const base = `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/analyses`
      const url = date ? `${base}?date=${encodeURIComponent(date)}` : base
      return apiFetch(url, CameraAnalysesResponse)
    },
    refetchInterval: date ? false : POLL_INTERVAL_MS,
  })

/** BL-005: current scene baseline for a camera. Returns null when the API
 *  responds 404 (no baseline yet) so the panel can render the empty state
 *  without surfacing a query error. */
export const cameraBaselineQuery = (siteId: string, cameraId: string) =>
  queryOptions({
    queryKey: queryKeys.cameraBaseline(siteId, cameraId),
    queryFn: async () => {
      try {
        return await apiFetch(
          `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/baseline`,
          CameraBaseline,
        )
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null
        throw err
      }
    },
    refetchInterval: POLL_INTERVAL_MS,
  })

/** WP2A4: the camera's configured Analysis Area, or null when the camera
 *  uses full-frame analysis (no area configured). Not polled -- this is
 *  only read while the editor drawer is open. */
export const cameraAnalysisAreaQuery = (siteId: string, cameraId: string) =>
  queryOptions({
    queryKey: queryKeys.cameraAnalysisArea(siteId, cameraId),
    queryFn: () =>
      apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/analysis-area`,
        AnalysisAreaResponse,
      ),
  })

/** The editor's saved pan/zoom for one view of this camera's photo
 *  ("grid" = the compact dashboard thumbnail, "detail" = the full camera
 *  page), or null when that view still uses its built-in default framing.
 *  Not polled -- read once per card/page and again while its editor is
 *  open. */
export const cameraDisplayCropQuery = (
  siteId: string,
  cameraId: string,
  context: DisplayCropContext,
) =>
  queryOptions({
    queryKey: queryKeys.cameraDisplayCrop(siteId, cameraId, context),
    queryFn: () =>
      apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/display-crop/${context}`,
        DisplayCropResponse,
      ),
  })

/** Camera Page UI/UX item 6: the camera's automatic-analysis window.
 *  Gated on camera.edit server-side (403 for a viewer) -- only rendered
 *  behind the Camera settings control, which itself checks useCan first.
 *  Not polled: read once when the settings panel opens. */
export const cameraAnalysisHoursQuery = (siteId: string, cameraId: string) =>
  queryOptions({
    queryKey: queryKeys.cameraAnalysisHours(siteId, cameraId),
    queryFn: () =>
      apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/analysis-hours`,
        AnalysisHours,
      ),
  })

/** Camera Page UI/UX item 4: the merged Activity history for one camera/
 *  local day (today by default). Replaces cameraAnalysesQuery +
 *  cameraEventsQuery on the camera page. Past days are immutable, so
 *  polling only applies to the (omitted date =) today view. */
export const cameraActivityQuery = (siteId: string, cameraId: string, date: string | null = null) =>
  queryOptions({
    queryKey: queryKeys.cameraActivity(siteId, cameraId, date),
    queryFn: () => {
      const base = `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/activity`
      const url = date ? `${base}?date=${encodeURIComponent(date)}` : base
      return apiFetch(url, CameraActivityResponse)
    },
    refetchInterval: date ? false : POLL_INTERVAL_MS,
  })

/** WP-SCHED: every schedule version for this site (draft/accepted/
 *  superseded/discarded), newest first, plus which id (if any) is the
 *  unreviewed draft and which is the currently accepted plan. Not
 *  polled -- read once per Project documents panel visit. */
export const scheduleVersionsQuery = (siteId: string) =>
  queryOptions({
    queryKey: queryKeys.scheduleVersions(siteId),
    queryFn: () =>
      apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/schedule-versions`,
        ScheduleVersionsResponse,
      ),
  })

// ── Chat ────────────────────────────────────────────────────────

export const chatHistoryQuery = (conversationId: string) =>
  queryOptions({
    queryKey: queryKeys.chatHistory(conversationId),
    queryFn: () =>
      apiFetch(
        `/api/chat/history?conversation_id=${encodeURIComponent(conversationId)}`,
        ChatHistoryResponse,
      ),
    // Item 4 ("is still running: show its actual progress state"): once
    // the server confirms a reply is genuinely still executing, poll
    // briefly so the UI picks up the real answer (or a genuine
    // interruption) as soon as it lands, instead of "Still working on
    // this reply..." sitting there until something else happens to
    // trigger a refetch. Plain GETs, not AI calls -- doesn't touch
    // item 6's "zero AI calls" budget for navigation/history/etc.
    refetchInterval: (query) => (query.state.data?.reply_status === 'running' ? 2000 : false),
  })

/** History panel list -- the caller's OWN conversations, most-recently-
 *  active first, scoped to `siteId` (null == the portfolio scope). Not
 *  polled; the History dropdown refetches on open. */
export const chatConversationsQuery = (siteId: string | null) =>
  queryOptions({
    queryKey: queryKeys.chatConversations(siteId),
    queryFn: () =>
      apiFetch(
        `/api/chat/conversations${siteId ? `?site_id=${encodeURIComponent(siteId)}` : ''}`,
        ChatConversationsResponse,
      ),
  })

// ── LLM stats ───────────────────────────────────────────────────

export const llmStatsQuery = (from: string | null, to: string | null) =>
  queryOptions({
    queryKey: queryKeys.llm(from, to),
    queryFn: () => {
      const params = new URLSearchParams()
      if (from) params.set('from', from)
      if (to) params.set('to', to)
      const qs = params.toString()
      return apiFetch(`/api/llm-stats${qs ? `?${qs}` : ''}`, LlmStats)
    },
  })

/** CDV-029: fetch a stashed composed-view payload by id. AppShell uses
 *  this when the URL is `?sala=ref:<id>`; the body is then re-parsed
 *  through parseSalaParam as if it had been inlined. */
export const stashedViewQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.stashedView(id),
    queryFn: () => apiFetch(`/api/chat/views/${encodeURIComponent(id)}`, StashedViewPayload),
    // Stash entries are immutable for their lifetime; no polling.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  })

// WP2A2: evidence + feedback -- fetched lazily, only when the "View
// evidence" drawer is actually opened, so the dashboard itself stays
// light. Not polled: the drawer is short-lived and re-fetches fresh each
// time it opens.

/** Single combined query for both evidence panels -- kept as one function
 *  (rather than two separately-typed queryOptions() results picked
 *  between by the caller) so TanStack Query only ever has to resolve one
 *  coherent options type, not a union of two incompatible ones. */
export const briefEvidenceQuery = (
  siteId: string,
  briefType: 'daily' | 'weekly',
  /** WEB-021/Site Update work order: the dashboard's selected date, so
   *  "View evidence" opened on a past day shows that day's (or that
   *  day's week's) evidence, not always today/this week. null (or
   *  omitted) means "today", matching every other date-aware query. */
  date: string | null = null,
) =>
  queryOptions({
    queryKey:
      briefType === 'daily'
        ? queryKeys.dailyBriefEvidence(siteId, date)
        : queryKeys.weeklySummaryEvidence(siteId, date),
    queryFn: () => {
      const segment = briefType === 'daily' ? 'daily-brief' : 'weekly-summary'
      const qs = date ? `?date=${encodeURIComponent(date)}` : ''
      return apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/${segment}/evidence${qs}`,
        BriefEvidence,
      )
    },
    // Zonoo: AI Photo Comparison -- the AI visual comparison for a new
    // image pair runs in the background (server schedules it, doesn't
    // block the response), so while it's in flight this re-fetches the
    // same GET (never a second paid call -- the server-side cache
    // reuses the in-flight row) every couple of seconds to pick up the
    // 'ready'/'failed' result. Stops polling as soon as the comparison
    // resolves, or when there's nothing to poll for.
    refetchInterval: (query) => (query.state.data?.comparison_status === 'pending' ? 2000 : false),
  })

export const briefFeedbackQuery = (
  siteId: string,
  briefType: 'daily' | 'weekly',
  periodStart: string,
  periodEnd: string,
) =>
  queryOptions({
    queryKey: queryKeys.briefFeedback(siteId, briefType, periodStart, periodEnd),
    queryFn: () =>
      apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/brief-feedback?brief_type=${briefType}` +
          `&period_start=${encodeURIComponent(periodStart)}&period_end=${encodeURIComponent(periodEnd)}`,
        BriefFeedback,
      ),
  })

/** 14 Oct 2026: Max's phase-start suggestions. Admin only; never fetched for clients. */
export const maxSuggestionsQuery = (siteId: string) =>
  queryOptions({
    queryKey: queryKeys.maxSuggestions(siteId),
    queryFn: () =>
      apiFetch(`/api/sites/${encodeURIComponent(siteId)}/max-suggestions`, MaxSuggestionsResponse),
  })

export const phaseSeenQuery = (siteId: string, phaseId: number) =>
  queryOptions({
    queryKey: queryKeys.phaseSeen(siteId, phaseId),
    queryFn: () =>
      apiFetch(
        `/api/sites/${encodeURIComponent(siteId)}/phases/${phaseId}/seen-this-week`,
        PhaseSeenResponse,
      ),
  })

export const siteWatchQuery = (siteId: string) =>
  queryOptions({
    queryKey: queryKeys.siteWatch(siteId),
    queryFn: () => apiFetch(`/api/sites/${encodeURIComponent(siteId)}/site-watch`, SiteWatchResponse),
    staleTime: 5 * 60 * 1000,
  })

export const sitePlanQuery = (siteId: string) =>
  queryOptions({
    queryKey: queryKeys.sitePlan(siteId),
    queryFn: () => apiFetch(`/api/sites/${encodeURIComponent(siteId)}/plan`, SitePlanResponse),
  })

export const liaQuery = (siteId: string, range: 'today' | 'week' = 'today') =>
  queryOptions({
    queryKey: queryKeys.lia(siteId, range),
    queryFn: () => apiFetch(`/api/sites/${encodeURIComponent(siteId)}/lia?range=${range}`, LiaMapResponse),
    staleTime: 5 * 60 * 1000,
  })

/** 24 Oct 2026: Max's one answer from the phases and the photos
 *  (server/max_status.py): on schedule / N days behind / timing may change. */
export const MaxStatus = z.object({
  tone: z.enum(['on', 'behind', 'check', 'done', 'none']),
  days: z.number(),
  why: z.object({ kind: z.string(), phase: z.string(), date: z.string() }).nullable(),
  pending: z.array(z.object({ phase_id: z.number(), kind: z.string(), phase: z.string(), date: z.string() })),
  days_if_confirmed: z.number().nullable(),
  as_of: z.string(),
})
export type MaxStatus = z.infer<typeof MaxStatus>

export function maxStatusQuery(siteId: string) {
  return queryOptions({
    queryKey: ['max-status', siteId],
    queryFn: () => apiFetch(`/api/sites/${encodeURIComponent(siteId)}/max-status`, MaxStatus),
    staleTime: 60_000,
  })
}
